import { createSlice } from '@reduxjs/toolkit';

import { Crumb } from '@/components/Breadcrumbs/BreadcrumbWrapper';
import {
    LOAD_CATEGORY_RESULTS_AUTH_REQUIRED,
    LOAD_CATEGORY_RESULTS_AUTH_REQUIRED_ACTION,
    LOAD_CATEGORY_RESULTS_FAIL,
    LOAD_CATEGORY_RESULTS_FAIL_ACTION,
    LOAD_CATEGORY_RESULTS_REQUEST,
    LOAD_CATEGORY_RESULTS_REQUEST_ACTION,
    LOAD_CATEGORY_RESULTS_SUCCESS,
    LOAD_CATEGORY_RESULTS_SUCCESS_ACTION,
    LOAD_DISCOVER_ITEMS_SUCCESS,
} from '@/redux/modules/actions';
import { setSearchExclusion } from '@/redux/modules/search/exclusions/searchExclusions.actions';
import { TypedActionWithPayload } from '@/types/redux';
import FacetTypes from '@/types/search/enums/facetTypes';

import {
    fetchAuctionHouseTypeahead,
    fetchFacetViewMore,
    fetchMoreSearchResults,
    fetchSearchAggregationsOnDemand,
    setIsZeroResults,
    setUseGeoLocation,
    submitSearch,
    updateSearchSellerItemsSearch,
    updateSearchSellerItemsSearchSellerId,
    updateSearchSubmittedFromPage,
} from './search.actions';
import {
    defaultSearchSlice,
    ElasticAggregateDemandStatus,
    SAVE_LAST_SEARCH_FETCH_OPTIONS,
    SearchResultPayload,
    SearchSlice,
} from './search.types';

type FacetWithOptions = { id?: string; options?: unknown[] };

/**
 * Merges a refetch response into the existing facet slice without losing
 * other facets the user previously expanded. The targeted facet (the one
 * whose facetOverride triggered the refetch) always wins from the response;
 * every other facet keeps whichever side has more options, since the
 * response brings back default-size lists for non-overridden facets.
 *
 * `unknown[]` on `options` because the reducer doesn't care about the
 * option shape -- it only needs to compare lengths.
 */
const mergeFacetsPreservingExpansions = <T extends FacetWithOptions>(
    existing: T[],
    incoming: T[],
    targetFacetId: string
): T[] => {
    const existingById = new Map<string | undefined, T>(existing.map((f) => [f.id, f]));
    return incoming.map((incomingFacet) => {
        if (incomingFacet.id === targetFacetId) {
            return incomingFacet;
        }
        const prev = existingById.get(incomingFacet.id);
        if (!prev) {
            return incomingFacet;
        }
        const prevLen = prev.options?.length ?? 0;
        const incomingLen = incomingFacet.options?.length ?? 0;
        return prevLen > incomingLen ? prev : incomingFacet;
    });
};

// Namespace the session anchor so a keyword slug can never collide with a category id (e.g. user types "1"
// while a separate flow browses category id 1) and to make the field self-documenting.
const getKeywordSessionKey = (searchTerm?: string) => (searchTerm ? `keyword:${searchTerm}` : undefined);

const resetSearchState = (slice: SearchSlice): SearchSlice => ({
    ...slice,
    aggregationsOnDemandStatus: ElasticAggregateDemandStatus.Idle,
    deepPageRequiresAuth: false,
    error: false,
    isZeroResults: slice.isZeroResults,
    seoSearchTerm: undefined,
    submitted: true,
    success: false,
});

const addSearchResults = (
    slice: SearchSlice,
    {
        currentQueryKey,
        dominantCategory,
        facets,
        itemIDs,
        itemScores,
        priorityFacets,
        queryModifications,
        seoSearchTerm,
        similarItemsURL,
        soldItemFacets,
        soldItemIDs,
        sort,
        spellCorrection,
        totalBuyNow,
        totalFound,
        totalLive,
        totalPages,
        totalSold,
    }: SearchResultPayload,
    // The session anchor comes from the dispatching action's args, NOT the
    // payload. `seoSearchTerm` is omitted on `seoSearch: false` responses
    // (used by the in-page search input), so it can't be relied on to
    // detect a new-keyword session — would have failed the QA regression
    // where typing into the search input left sticky pointing at the
    // previous keyword's pills.
    sessionKey?: string
): SearchSlice => {
    if (typeof itemIDs === 'undefined' && typeof soldItemIDs === 'undefined') {
        // if we are just passing an empty object because we dont want to reload then we just set some properties and call it good
        return {
            ...slice,
            error: false,
            submitted: false,
            success: true,
        };
    }
    // Absent / null means "keep existing" so a late-arriving skipAggs response
    // cannot clobber a parallel fetchSearchAggregationsOnDemand.fulfilled.
    const nextFacets = facets === null || typeof facets === 'undefined' ? slice.facets : facets;
    const nextSoldItemFacets =
        soldItemFacets === null || typeof soldItemFacets === 'undefined' ? slice.soldItemFacets : soldItemFacets;
    // Absent / null / non-array means "keep existing" so partial payloads
    // can't clobber an earlier fulfilled value. Empty array is a real signal
    // ("no priority facets this query") and overwrites.
    const nextPriorityFacets = Array.isArray(priorityFacets) ? priorityFacets : slice.priorityFacets;

    // Sticky: persist the last non-empty list while the user refines the same
    // keyword session, reset on a different keyword so previous-keyword pills
    // don't leak. The session anchor is the dispatching action's `searchTerm`
    // (URL slug, always defined for keyword dispatches) — NOT the payload's
    // `seoSearchTerm.term`, which is missing whenever the request uses
    // `seoSearch: false` (in-page search input path). An undefined sessionKey
    // means "non-keyword dispatch" (category browse / discover) and preserves
    // the current session so browse-path refinements keep their sticky.
    // A corrected zero-results search preloads the corrected keyword's sold lots, which continues the
    // typed keyword's session rather than starting a new one.
    const isCorrectedSoldItemsPreload =
        slice.isZeroResults &&
        slice.spellCorrection?.isCorrected === true &&
        sessionKey !== undefined &&
        sessionKey === getKeywordSessionKey(slice.spellCorrection.correctedQuery);
    const isNewSession =
        sessionKey !== undefined && sessionKey !== slice.stickyTermAnchor && !isCorrectedSoldItemsPreload;

    // Sticky, same shape as priorityFacets: a present value wins; absence on a
    // new keyword session clears the stale category; absence within the same
    // session (filtered query / aggsOnly refetch gates the backend meta off)
    // keeps the session's value so the cached-result event still reports it.
    let nextDominantCategory: SearchSlice['dominantCategory'];
    if (dominantCategory !== undefined) {
        nextDominantCategory = dominantCategory;
    } else if (isNewSession) {
        nextDominantCategory = undefined;
    } else {
        nextDominantCategory = slice.dominantCategory;
    }

    // Absence clears, except on the same keyword's zero-results page: the sold-items preload (and
    // its page / sort changes) never asks search-party for a correction, yet the suggestion still
    // describes what the person typed.
    const isSameKeywordZeroResults = slice.isZeroResults && sessionKey !== undefined && !isNewSession;
    const nextSpellCorrection = spellCorrection ?? (isSameKeywordZeroResults ? slice.spellCorrection : undefined);

    let nextStickyPriorityFacets: string[];
    if (isNewSession) {
        nextStickyPriorityFacets = nextPriorityFacets;
    } else if (nextPriorityFacets.length > 0) {
        nextStickyPriorityFacets = nextPriorityFacets;
    } else {
        nextStickyPriorityFacets = slice.stickyPriorityFacets;
    }

    return {
        ...slice,
        currentQueryKey,
        dominantCategory: nextDominantCategory,
        facets: nextFacets,
        isInitialLoading: false,
        isLoading: false,
        itemIds: itemIDs,
        itemScores,
        priorityFacets: nextPriorityFacets,
        stickyPriorityFacets: nextStickyPriorityFacets,
        stickyTermAnchor: sessionKey ?? slice.stickyTermAnchor,
        queryModifications,
        seoSearchTerm: seoSearchTerm
            ? {
                  indexable: seoSearchTerm.indexable,
                  metaDescription: seoSearchTerm.metaDescription,
                  pageTitle: seoSearchTerm.h1Header,
                  relatedTerms: seoSearchTerm.relatedTerms,
                  seoCopy: seoSearchTerm.seoCopy,
                  seoTitle: seoSearchTerm.seoTitle,
                  tabName: seoSearchTerm.pageTitle,
                  term: seoSearchTerm.term,
              }
            : undefined,
        similarItemsURL,
        soldItemFacets: nextSoldItemFacets,
        soldItemIds: soldItemIDs ?? slice.soldItemIds,
        sort,
        spellCorrection: nextSpellCorrection,
        submitted: false,
        success: true,
        totalBuyNowItems: totalBuyNow,
        totalFound,
        totalLiveItems: totalLive ?? slice.totalLiveItems,
        totalPages,
        totalSoldItems: totalSold ?? slice.totalSoldItems,
    };
};

const enterErrorState = (slice: SearchSlice): SearchSlice => ({
    ...slice,
    error: true,
    seoSearchTerm: undefined,
    submitted: false,
    success: false,
});

// Distinct from enterErrorState: also settles isLoading/isInitialLoading/submitted
// (normally only cleared by a successful addSearchResults) so consumers gating UI
// on those flags -- e.g. CategorySearchPage's breadcrumb render -- don't stay stuck
// as if the request never finished.
const enterAuthRequiredState = (slice: SearchSlice): SearchSlice => ({
    ...slice,
    deepPageRequiresAuth: true,
    isInitialLoading: false,
    isLoading: false,
    seoSearchTerm: undefined,
    submitted: false,
    success: false,
});

const searchSlice = createSlice({
    extraReducers: (builder) => {
        builder.addCase(SAVE_LAST_SEARCH_FETCH_OPTIONS, (state, action: any) => {
            state.lastSearchFetchOptions = action.payload;
        });

        builder.addCase(submitSearch.pending, (state, action) => {
            const resetState = resetSearchState(state);
            // Re-running the keyword already on screen (Back navigation, a refetch after login) can be answered
            // from the search cache, whose fulfilled payload is empty and never restores seoSearchTerm. Keep it
            // for the same keyword so the page doesn't lose its SEO title, related searches and robots; a real
            // response still replaces it, and a different keyword still starts clean.
            const sessionKey = getKeywordSessionKey(action.meta?.arg?.searchQuery?.searchTerm);
            const isSameKeyword = sessionKey !== undefined && sessionKey === state.stickyTermAnchor;
            return isSameKeyword ? { ...resetState, seoSearchTerm: state.seoSearchTerm } : resetState;
        });
        builder.addCase<typeof LOAD_CATEGORY_RESULTS_REQUEST, LOAD_CATEGORY_RESULTS_REQUEST_ACTION>(
            LOAD_CATEGORY_RESULTS_REQUEST,
            (state) => resetSearchState({ ...state, categoryCrumbs: [] })
        );
        builder.addCase(fetchMoreSearchResults.pending, resetSearchState);

        builder.addCase(submitSearch.fulfilled, (state, action) => {
            const sessionKey = getKeywordSessionKey(action.meta?.arg?.searchQuery?.searchTerm);
            return addSearchResults(state, action.payload as SearchResultPayload, sessionKey);
        });
        builder.addCase<
            typeof LOAD_DISCOVER_ITEMS_SUCCESS,
            TypedActionWithPayload<typeof LOAD_DISCOVER_ITEMS_SUCCESS, SearchResultPayload>
        >(LOAD_DISCOVER_ITEMS_SUCCESS, (state, { payload }) => addSearchResults(state, payload));
        builder.addCase<typeof LOAD_CATEGORY_RESULTS_SUCCESS, LOAD_CATEGORY_RESULTS_SUCCESS_ACTION>(
            LOAD_CATEGORY_RESULTS_SUCCESS,
            (state, { payload }) => {
                if (payload.soldItemIDs || payload.itemIDs) {
                    // Category-nav session anchor is the L1 category id from the
                    // payload's first breadcrumb. Same L1 across refinements
                    // (e.g. /c/art/1/ → /c/art/painting/10/) preserves sticky;
                    // a different L1 (/c/art/1/ → /c/jewelry/6/) resets it.
                    const categoryId = payload.breadcrumbs?.[0]?.id;
                    const sessionKey = categoryId ? `category:${categoryId}` : undefined;
                    return addSearchResults(
                        {
                            ...state,
                            categoryCrumbs:
                                payload.breadcrumbs?.map(
                                    (crumb): Crumb => ({
                                        label: crumb.name,
                                        link: crumb.url,
                                    })
                                ) || [],
                        },
                        payload,
                        sessionKey
                    );
                }
            }
        );

        // fetchSearchAggregationsOnDemand: only update facets, NOT items/loading/submitted
        builder.addCase(fetchSearchAggregationsOnDemand.pending, (state) => {
            state.aggregationsOnDemandStatus = ElasticAggregateDemandStatus.Loading;
        });

        builder.addCase(fetchSearchAggregationsOnDemand.fulfilled, (state, { payload }) => {
            if (payload && typeof payload === 'object' && 'facets' in payload) {
                // On-demand fetch always asks for full aggs, so payload.facets is populated.
                // Fall back to [] for the `null` branch that exists only for skipAggs initial loads.
                state.facets = payload.facets ?? [];
                state.soldItemFacets = payload.soldItemFacets;
            }
            state.aggregationsOnDemandStatus = ElasticAggregateDemandStatus.Loaded;
        });

        builder.addCase(fetchSearchAggregationsOnDemand.rejected, (state) => {
            state.aggregationsOnDemandStatus = ElasticAggregateDemandStatus.Error;
        });

        // fetchFacetViewMore + fetchAuctionHouseTypeahead both re-fetch the
        // whole search with one facet's override, but the response carries
        // every facet at its default size. Replacing `state.facets`
        // wholesale wipes any *other* facet the user had previously
        // expanded -- e.g. expand Creator (100), then expand Material; the
        // Material response only has Creator(6) so Creator shrinks back to
        // the default and the user loses their View More state. Merge
        // strategy: trust the targeted facet from the response, and for
        // every other facet keep whichever side has more options. That
        // preserves prior expansions without leaking stale data when a
        // facet legitimately shrinks (e.g. filter applied).
        //
        // Both cases intentionally avoid touching `aggregationsOnDemandStatus`
        // -- that flag gates a full-drawer overlay which is fine for the
        // one-shot skipAggs hydration but disastrous for a per-toggle View
        // More click.
        builder.addCase(fetchFacetViewMore.fulfilled, (state, { payload, meta }) => {
            if (payload && typeof payload === 'object' && 'facets' in payload) {
                state.facets = mergeFacetsPreservingExpansions(state.facets, payload.facets ?? [], meta.arg.facetId);
                state.soldItemFacets = payload.soldItemFacets;
            }
        });

        // fetchAuctionHouseTypeahead tracks its loading on a dedicated slice
        // field so the AuctionHouseFilter's in-section spinner can fire on
        // every keystroke without hiding the entire drawer.
        builder.addCase(fetchAuctionHouseTypeahead.pending, (state) => {
            state.auctionHouseTypeaheadStatus = ElasticAggregateDemandStatus.Loading;
        });

        builder.addCase(fetchAuctionHouseTypeahead.fulfilled, (state, { payload }) => {
            if (payload && typeof payload === 'object' && 'facets' in payload) {
                state.facets = mergeFacetsPreservingExpansions(
                    state.facets,
                    payload.facets ?? [],
                    FacetTypes.AUCTION_HOUSE_NAME_ID
                );
                state.soldItemFacets = payload.soldItemFacets;
            }
            state.auctionHouseTypeaheadStatus = ElasticAggregateDemandStatus.Loaded;
        });

        builder.addCase(fetchAuctionHouseTypeahead.rejected, (state) => {
            state.auctionHouseTypeaheadStatus = ElasticAggregateDemandStatus.Error;
        });

        builder.addCase(fetchMoreSearchResults.fulfilled, (state, action) => {
            const payload = action.payload as SearchResultPayload;
            const { itemIDs } = payload;
            const allItemIds = [...state.itemIds];
            itemIDs.forEach((id) => {
                if (!allItemIds.includes(id)) {
                    allItemIds.push(id);
                }
            });
            const updatedIdsPayload: SearchResultPayload = {
                ...payload,
                itemIDs: allItemIds,
            };
            const sessionKey = getKeywordSessionKey(action.meta?.arg?.searchQuery?.searchTerm);
            return addSearchResults(state, updatedIdsPayload, sessionKey);
        });

        builder.addCase(setUseGeoLocation.fulfilled, (state, { payload }) => {
            state.useGeoLocation = payload;
        });

        builder.addCase(updateSearchSubmittedFromPage.fulfilled, (state, { payload }) => {
            state.submittedFromPage = payload;
        });

        builder.addCase(updateSearchSellerItemsSearch.fulfilled, (state, { payload }) => {
            state.sellerItemsSearch = payload;
        });

        builder.addCase(updateSearchSellerItemsSearchSellerId.fulfilled, (state, { payload }) => {
            state.sellerItemsSearchSellerId = payload;
        });

        builder.addCase(setIsZeroResults.fulfilled, (state, { payload }) => {
            state.isZeroResults = payload;
        });

        builder.addCase(setSearchExclusion.fulfilled, (state) => {
            state.aggregationsOnDemandStatus = ElasticAggregateDemandStatus.Idle;
        });

        builder.addCase(fetchMoreSearchResults.rejected, enterErrorState);
        builder.addCase(submitSearch.rejected, (state, action) =>
            action.payload?.deepPageRequiresAuth ? enterAuthRequiredState(state) : enterErrorState(state)
        );
        builder.addCase<typeof LOAD_CATEGORY_RESULTS_FAIL, LOAD_CATEGORY_RESULTS_FAIL_ACTION>(
            LOAD_CATEGORY_RESULTS_FAIL,
            (state) => {
                // On fail, clear possible crumbs on page.
                return enterErrorState({ ...state, categoryCrumbs: [] });
            }
        );
        // Deliberately does NOT clear categoryCrumbs (unlike LOAD_CATEGORY_RESULTS_FAIL
        // above) -- the sign-in interstitial keeps breadcrumbs visible.
        builder.addCase<typeof LOAD_CATEGORY_RESULTS_AUTH_REQUIRED, LOAD_CATEGORY_RESULTS_AUTH_REQUIRED_ACTION>(
            LOAD_CATEGORY_RESULTS_AUTH_REQUIRED,
            enterAuthRequiredState
        );
    },
    initialState: defaultSearchSlice,
    name: 'search',
    reducers: {},
});

export const { actions: searchActions, reducer: searchReducer } = searchSlice;
